import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Navigate, useNavigate } from 'react-router-dom';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import UserService from '../../services/UserService';
import SuperAdminService from '../../services/SuperAdminService';
import FilledButton from '../../components/Buttons/FilledButton';
import { USER_ROLES } from '../../constants/main';
import Pagination from '../../components/Pagination';
import Th from '../../components/Th';
import { useUiStore } from '../../store';
import { UiContext } from '../../context/UiContext';
import { saveAuthData } from '../../helpers/authStorage';

import classes from './styles.module.scss';

const ITEMS_PER_PAGE = 10;

export default function SuperAdminPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);

  const { data: currentUser, isLoading: isLoadingUser } = useQuery(
    'me',
    UserService.getMe
  );

  const {
    data: organizationsResponse,
    isLoading: isLoadingOrganizations,
    isFetching: isFetchingOrganizations,
  } = useQuery(
    ['super-admin-organizations', page, search],
    () =>
      SuperAdminService.getOrganizations({
        page,
        perPage: ITEMS_PER_PAGE,
        search: search ? search.trim() : undefined,
      }),
    {
      enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN,
      keepPreviousData: true,
    }
  );

  const organizations = useMemo(
    () => organizationsResponse?.data || [],
    [organizationsResponse]
  );
  const paginationMeta = organizationsResponse?.meta;

  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;
  const impersonation = currentUser?.impersonation;

  const setBreadcrumbsOptions = useUiStore(
    (state) => state.setBreadcrumbsOptions
  );

  useEffect(() => {
    setBreadcrumbsOptions([{ label: 'Super Admin', to: '/super-admin' }]);
  }, [setBreadcrumbsOptions]);

  const handleStopImpersonation = useCallback(async () => {
    if (isStoppingImpersonation) {
      return;
    }

    setIsStoppingImpersonation(true);
    try {
      const response = await SuperAdminService.endImpersonation();

      saveAuthData({
        userId: response.userId,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        name: response.name,
      });
      queryClient.invalidateQueries('me');
      notifySuccess('Impersonation stopped');
      setIsStoppingImpersonation(false);
      window.location.href = '/super-admin';
    } catch (error) {
      setIsStoppingImpersonation(false);
      notifyError(
        error?.response?.data?.message || 'Failed to stop impersonation'
      );
    }
  }, [isStoppingImpersonation, queryClient, notifySuccess, notifyError]);

  if (!isLoadingUser && !isSuperAdmin) {
    return <Navigate to="/my-videos" replace />;
  }

  const activeImpersonationName =
    impersonation?.actingOrganizationId &&
    organizations.find((org) => org.id === impersonation.actingOrganizationId)
      ? organizations.find(
          (org) => org.id === impersonation.actingOrganizationId
        )?.name
      : 'Organization Admin';

  const renderTableContent = () => {
    if (isLoadingOrganizations) {
      return (
        <div className={classes.loadingState}>Loading organizations…</div>
      );
    }

    if (!organizations.length) {
      return (
        <div className={classes.emptyState}>
          No organizations found. Try adjusting your search.
        </div>
      );
    }

    return (
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <Th
                classes={classes}
                sortField={null}
                sortOrder={null}
                setSortField={() => {}}
                setSortOrder={() => {}}
                field="name"
                title="Name"
                noSort
              />
              <Th
                classes={classes}
                sortField={null}
                sortOrder={null}
                setSortField={() => {}}
                setSortOrder={() => {}}
                field="location"
                title="Location"
                noSort
              />
              <Th
                classes={classes}
                sortField={null}
                sortOrder={null}
                setSortField={() => {}}
                setSortOrder={() => {}}
                field="created"
                title="Created"
                noSort
              />
              <Th
                classes={classes}
                sortField={null}
                sortOrder={null}
                setSortField={() => {}}
                setSortOrder={() => {}}
                field="actions"
                title="Actions"
                noSort
              />
            </tr>
          </thead>
          <tbody>
            {organizations.map((organization) => (
              <tr
                key={organization.id}
                className={classes.tableRow}
                onClick={() => navigate(`/super-admin/organizations/${organization.id}`)}
              >
                <td className={classes.nameCell}>
                  <div className={classes.nameContent}>
                    <h3>{organization.name}</h3>
                    {impersonation?.actingOrganizationId === organization.id && (
                      <span className={classes.statusBadge} data-status="active">Active</span>
                    )}
                  </div>
                </td>
                <td className={classes.locationCell}>
                  {organization.city}, {organization.state} {organization.zip}
                </td>
                <td className={classes.dateCell}>
                  {new Date(organization.createdAt).toLocaleDateString()}
                </td>
                <td className={classes.actionsCell}>
                  <div className={classes.actionsButtons}>
                    <FilledButton
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/super-admin/organizations/${organization.id}/edit`);
                      }}
                    >
                      Edit
                    </FilledButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Layout
      hasUserBlock
      hasSearch
      searchInput={search}
      setSearchInput={setSearch}
      searchPlaceholder="Search organizations"
    >
      <div className={classes.SuperAdminPage}>
        <Navigation />
        <div className={classes.container}>
          <div className={classes.breadcrumbsContainer}>
            <Breadcrumbs
              breadcrumbsOptions={[{ label: 'Super Admin', to: '/super-admin' }]}
            />
            <p className={classes.subtitle}>
              Manage organizations, invite owners, and impersonate any account to
              help them succeed.
            </p>
          </div>

          {impersonation?.isImpersonating && (
            <div className={classes.impersonationBanner}>
              <div>
                <strong>Impersonating</strong> {activeImpersonationName}
              </div>
              <button
                type="button"
                className={classes.bannerButton}
                onClick={handleStopImpersonation}
                disabled={isStoppingImpersonation}
              >
                {isStoppingImpersonation ? 'Ending…' : 'Return to Super Admin'}
              </button>
            </div>
          )}

          <div className={classes.actionsRow}>
            <FilledButton onClick={() => navigate('/super-admin/organizations/new')}>
              Create Organization
            </FilledButton>
            <FilledButton
              onClick={() => navigate('/super-admin/tiers')}
              className={classes.secondaryButton}
            >
              Manage Tiers
            </FilledButton>
          </div>

          <section className={classes.sectionCard}>
            <div className={classes.sectionHeader}>
              <div>
                <h3>Organizations</h3>
                <p>
                  {isFetchingOrganizations
                    ? 'Refreshing…'
                    : 'Latest data synced'}
                </p>
              </div>
            </div>

            <div className={classes.tableContainer}>
              {renderTableContent()}
            </div>

            {paginationMeta && paginationMeta.total > ITEMS_PER_PAGE && (
              <div className={classes.pagination}>
                <Pagination
                  activePage={page}
                  itemsCountPerPage={ITEMS_PER_PAGE}
                  totalItemsCount={paginationMeta.total}
                  onChange={(nextPage) => setPage(nextPage)}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
